import React, { useEffect, useState } from "react";
import { Modal } from "../../../components/Modal";
import { Notification } from "../../../components/Notification";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { ModoHistoriaDto } from "../../../dto/modo-historia.dto";
import {
  ModoHistoriaInputDto,
  ModoHistoriaService,
} from "../../../services/admin/modo-historia";
import { AdminLayout } from "../components";
import { BulkEditModal } from "../components/BulkEditModal";
import { ImportarJSONModalModoHistoria } from "../components/ImportarJSONModalModoHistoria";
import {
  ModoHistoriaFilter,
  ModoHistoriaFilterOptions,
} from "../components/ModoHistoriaFilter";
import PreguntaOficialDetailModal from "../components/PreguntaOficialDetailModal";
import PreguntasOficialesTable from "../components/PreguntasOficialesTable";

interface NotificationState {
  isOpen: boolean;
  type: "success" | "error" | "info" | "warning";
  title: string;
  message: string;
}

export const ModoHistoriaPage: React.FC = () => {
  const [preguntas, setPreguntas] = useState<ModoHistoriaDto[]>([]);
  const [mundos, setMundos] = useState<any[]>([]);
  const [etapas, setEtapas] = useState<any[]>([]);
  const [etapasParaEdicion, setEtapasParaEdicion] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [reordenarDespuesEdicion, setReordenarDespuesEdicion] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [formData, setFormData] = useState({
    contenido: "",
    alternativas: { A: "", B: "", C: "", D: "" } as Record<string, string>,
    respuesta: "",
    explicacion: "",
    mundoHistoriaId: "",
    etapaHistoriaId: "",
    preguntaOrden: 1,
    vigente: true,
    revisada: false,
    tieneImagen: false,
    imagen: null as File | null,
    datosGrafico: "",
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedMundo, setSelectedMundo] = useState<number | null>(null);
  const [editingPregunta, setEditingPregunta] =
    useState<ModoHistoriaDto | null>(null);

  // Estado para filtros
  const [filters, setFilters] = useState<ModoHistoriaFilterOptions>({
    searchText: "",
    selectedMundo: "",
    selectedEtapa: "",
    vigenciaFilter: "vigentes",
    tieneGraficoFilter: "todos",
  });

  // Estado para modal de importación JSON
  const [showImportModal, setShowImportModal] = useState(false);

  // Estado para notificaciones
  const [notification, setNotification] = useState<NotificationState>({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
  });

  // Estados para confirmación de eliminación
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [preguntaToDelete, setPreguntaToDelete] =
    useState<ModoHistoriaDto | null>(null);

  // Funciones para manejar notificaciones
  const showNotification = (
    type: "success" | "error" | "info" | "warning",
    title: string,
    message: string
  ) => {
    setNotification({
      isOpen: true,
      type,
      title,
      message,
    });
  };

  const closeNotification = () => {
    setNotification((prev) => ({ ...prev, isOpen: false }));
  };

  // Cargar datos iniciales
  const cargarDatos = async () => {
    try {
      setLoading(true);
      const [preguntasData, mundosData, etapasData] = await Promise.all([
        ModoHistoriaService.getPreguntasOficiales(),
        ModoHistoriaService.getMundos(),
        ModoHistoriaService.getEtapas(),
      ]);

      // El backend ya devuelve las preguntas ordenadas correctamente
      // No necesitamos reordenar aquí, confiamos en el orden del backend
      setPreguntas(preguntasData);
      setMundos(mundosData);
      setEtapas(etapasData);
      setReordenarDespuesEdicion(false);
    } catch (error: any) {
      setError(error.message || "Error al cargar los datos");
      showNotification(
        "error",
        "Error",
        error.message || "Error al cargar los datos"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setFormData({
      contenido: "",
      alternativas: { A: "", B: "", C: "", D: "" },
      respuesta: "",
      explicacion: "",
      mundoHistoriaId: "",
      etapaHistoriaId: "",
      preguntaOrden: 1,
      vigente: true,
      revisada: false,
      tieneImagen: false,
      imagen: null,
      datosGrafico: "",
    });
    setIsEditing(false);
    setEditingId(null);
    setEditingPregunta(null);
    setSelectedMundo(null);
    setEtapasParaEdicion([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      if (isEditing && editingId) {
        // Para actualización, no enviar solo campos que cambiaron, sino los campos críticos siempre
        // para asegurar que el backend devuelva todos los datos correctamente
        const updateData: Partial<ModoHistoriaInputDto> = {};

        // Campos que siempre se deben enviar para mantener consistencia
        updateData.mundoHistoriaId = Number(formData.mundoHistoriaId);
        updateData.etapaHistoriaId = Number(formData.etapaHistoriaId);
        updateData.preguntaOrden = formData.preguntaOrden;

        // Solo agregar campos que realmente cambiaron (para optimización)
        if (formData.contenido !== editingPregunta?.contenido) {
          updateData.contenido = formData.contenido;
        }
        if (
          JSON.stringify(formData.alternativas) !==
          JSON.stringify(editingPregunta?.alternativas)
        ) {
          updateData.alternativas = formData.alternativas;
        }
        if (formData.respuesta !== editingPregunta?.respuesta) {
          updateData.respuesta = formData.respuesta;
        }
        if (formData.explicacion !== editingPregunta?.explicacion) {
          updateData.explicacion = formData.explicacion;
        }
        if (formData.vigente !== editingPregunta?.vigente) {
          updateData.vigente = formData.vigente;
        }
        if (formData.revisada !== editingPregunta?.revisada) {
          updateData.revisada = formData.revisada;
        }
        if (formData.imagen) {
          updateData.imagen = formData.imagen;
        }
        if (formData.datosGrafico) {
          // Si ya es un objeto, usarlo directamente; si es string, parsearlo
          updateData.datosGrafico =
            typeof formData.datosGrafico === "string"
              ? JSON.parse(formData.datosGrafico)
              : formData.datosGrafico;
        }

        // Solo enviar si hay cambios
        if (Object.keys(updateData).length === 0) {
          showNotification("info", "Info", "No hay cambios para guardar");
          setIsSaving(false);
          closeModal();
          return;
        }

        // Guardar si cambió el orden para recalcular la página
        const ordenCambio =
          formData.preguntaOrden !== editingPregunta?.orden ||
          Number(formData.mundoHistoriaId) !==
            editingPregunta?.mundoHistoriaId ||
          Number(formData.etapaHistoriaId) !== editingPregunta?.etapaHistoriaId;

        const updatedPregunta = await ModoHistoriaService.updatePreguntaOficial(
          editingId,
          updateData
        );

        // Asegurar que updatedPregunta tenga todos los campos necesarios
        // Si el backend no devuelve mundoHistoriaId, usar el del formData
        const preguntaCompleta: ModoHistoriaDto = {
          ...updatedPregunta,
          mundoHistoriaId:
            updatedPregunta.mundoHistoriaId || Number(formData.mundoHistoriaId),
          orden: (updatedPregunta as any).orden || formData.preguntaOrden,
          revisada:
            (updatedPregunta as any).revisada ??
            editingPregunta?.revisada ??
            false,
        };

        showNotification(
          "success",
          "Éxito",
          "Pregunta oficial actualizada correctamente"
        );

        // Actualizar solo la pregunta específica sin cambiar su posición si no cambió el orden
        setPreguntas((prevPreguntas) => {
          // Buscar el índice actual de la pregunta antes de actualizar
          const preguntaIndexActual = prevPreguntas.findIndex(
            (p) => p.idPreguntaOficial === editingId
          );

          // Actualizar la pregunta específica manteniendo su posición exacta
          const updated = [...prevPreguntas];
          if (preguntaIndexActual >= 0) {
            updated[preguntaIndexActual] = preguntaCompleta;
          } else {
            // Si no se encuentra, usar map como fallback
            return prevPreguntas.map((p) =>
              p.idPreguntaOficial === editingId ? preguntaCompleta : p
            );
          }

          // Si cambió el orden (mundo, etapa o preguntaOrden), reordenar todo
          if (ordenCambio) {
            setReordenarDespuesEdicion(true);
            const reordered = sortPreguntas(updated);

            // Encontrar en qué página quedó la pregunta actualizada
            const preguntaIndexNuevo = reordered.findIndex(
              (p) => p.idPreguntaOficial === editingId
            );
            if (preguntaIndexNuevo >= 0) {
              const nuevaPagina =
                Math.floor(preguntaIndexNuevo / itemsPerPage) + 1;
              setCurrentPage(nuevaPagina);
            }

            return reordered;
          }

          // Si no cambió el orden, mantener la pregunta en su posición actual exacta
          // NO reordenar, solo actualizar el objeto en su posición
          setReordenarDespuesEdicion(false);
          return updated;
        });

        // Actualizar también editingPregunta y formData para que el formulario tenga los datos actualizados
        setEditingPregunta(preguntaCompleta);

        // Actualizar formData con los valores actualizados si el modal sigue abierto
        if (isModalOpen) {
          setFormData({
            contenido: preguntaCompleta.contenido,
            alternativas: preguntaCompleta.alternativas,
            respuesta: preguntaCompleta.respuesta,
            explicacion: preguntaCompleta.explicacion,
            mundoHistoriaId: preguntaCompleta.mundoHistoriaId
              ? preguntaCompleta.mundoHistoriaId.toString()
              : "",
            etapaHistoriaId: preguntaCompleta.etapaHistoriaId.toString(),
            preguntaOrden: preguntaCompleta.orden || 1,
            vigente: preguntaCompleta.vigente ?? true,
            revisada: preguntaCompleta.revisada ?? false,
            tieneImagen: preguntaCompleta.tieneImagen ?? false,
            imagen: null,
            datosGrafico: preguntaCompleta.datosGrafico
              ? typeof preguntaCompleta.datosGrafico === "string"
                ? preguntaCompleta.datosGrafico
                : JSON.stringify(preguntaCompleta.datosGrafico)
              : "",
          });

          // Actualizar también las etapas disponibles si cambió el mundo
          if (preguntaCompleta.mundoHistoriaId) {
            setSelectedMundo(preguntaCompleta.mundoHistoriaId);
            const etapasDelMundo = etapas.filter(
              (etapa) => etapa.mundoId === preguntaCompleta.mundoHistoriaId
            );
            setEtapasParaEdicion(etapasDelMundo);
          }
        }
      } else {
        // Para creación, enviar todos los campos necesarios
        const createData: ModoHistoriaInputDto = {
          contenido: formData.contenido,
          alternativas: formData.alternativas,
          respuesta: formData.respuesta,
          explicacion: formData.explicacion,
          mundoHistoriaId: Number(formData.mundoHistoriaId),
          etapaHistoriaId: Number(formData.etapaHistoriaId),
          preguntaOrden: formData.preguntaOrden,
          vigente: formData.vigente,
          imagen: formData.imagen || undefined,
          datosGrafico: formData.datosGrafico
            ? typeof formData.datosGrafico === "string"
              ? JSON.parse(formData.datosGrafico)
              : formData.datosGrafico
            : undefined,
        };

        const newPregunta = await ModoHistoriaService.createPreguntaOficial(
          createData
        );
        showNotification(
          "success",
          "Éxito",
          "Pregunta oficial creada correctamente"
        );

        // Agregar la nueva pregunta al final de la lista
        setPreguntas((prevPreguntas) => {
          return [...prevPreguntas, newPregunta];
        });
      }
      setIsSaving(false);
      closeModal();
    } catch (error: any) {
      setIsSaving(false);
      showNotification(
        "error",
        "Error",
        error.response?.data?.message || "Error al procesar la solicitud"
      );
    }
  };

  const handleEdit = (pregunta: ModoHistoriaDto) => {
    setIsEditing(true);
    setEditingId(pregunta.idPreguntaOficial);
    setEditingPregunta(pregunta);

    // Establecer el formulario con los datos de la pregunta ANTES de abrir el modal
    const formDataToSet = {
      contenido: pregunta.contenido,
      alternativas: pregunta.alternativas,
      respuesta: pregunta.respuesta,
      explicacion: pregunta.explicacion,
      mundoHistoriaId: pregunta.mundoHistoriaId
        ? pregunta.mundoHistoriaId.toString()
        : "",
      etapaHistoriaId: pregunta.etapaHistoriaId.toString(),
      preguntaOrden: pregunta.orden || 1,
      vigente: pregunta.vigente ?? true,
      revisada: pregunta.revisada ?? false,
      tieneImagen: pregunta.tieneImagen ?? false,
      imagen: null, // No cargamos la imagen en el formulario de edición
      datosGrafico: pregunta.datosGrafico
        ? typeof pregunta.datosGrafico === "string"
          ? pregunta.datosGrafico
          : JSON.stringify(pregunta.datosGrafico)
        : "",
    };
    setFormData(formDataToSet);

    // Cargar las etapas del mundo seleccionado ANTES de abrir el modal
    if (pregunta.mundoHistoriaId) {
      setSelectedMundo(pregunta.mundoHistoriaId);
      // Filtrar etapas del mundo seleccionado
      const etapasDelMundo = etapas.filter(
        (etapa) => etapa.mundoId === pregunta.mundoHistoriaId
      );
      setEtapasParaEdicion(etapasDelMundo);
    } else {
      setEtapasParaEdicion(etapas);
    }

    // Abrir el modal después de establecer todos los datos
    setIsModalOpen(true);
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
    setEtapasParaEdicion(etapas);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleMundoChange = (mundoId: number) => {
    setSelectedMundo(mundoId);

    // Filtrar etapas del mundo seleccionado
    const etapasDelMundo = etapas.filter((etapa) => etapa.mundoId === mundoId);
    setEtapasParaEdicion(etapasDelMundo);

    // Verificar si la etapa actual pertenece al nuevo mundo
    const etapaActual = formData.etapaHistoriaId;
    if (etapaActual) {
      const etapaExiste = etapasDelMundo.some(
        (etapa) => etapa.idEtapaHistoria.toString() === etapaActual
      );

      if (!etapaExiste) {
        // Limpiar la etapa si no pertenece al nuevo mundo
        setFormData((prev) => ({ ...prev, etapaHistoriaId: "" }));
      }
    }
  };

  const getMundoName = (mundoId: number) => {
    const mundo = mundos.find((m) => m.idMundoHistoria === mundoId);
    return mundo?.nombre || "N/A";
  };

  const getEtapaName = (etapaId: number) => {
    const etapa = etapas.find((e) => e.idEtapaHistoria === etapaId);
    return etapa?.nombre || "N/A";
  };

  // Funciones para manejar acciones de la tabla
  const handleView = (pregunta: ModoHistoriaDto) => {
    setEditingPregunta(pregunta);
    setIsViewModalOpen(true);
  };

  const handleDeleteClick = (pregunta: ModoHistoriaDto) => {
    setPreguntaToDelete(pregunta);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!preguntaToDelete) return;

    try {
      await ModoHistoriaService.deletePreguntaOficial(
        preguntaToDelete.idPreguntaOficial
      );
      showNotification(
        "success",
        "Éxito",
        "Pregunta oficial desactivada correctamente"
      );
      cargarDatos();
      setShowDeleteConfirm(false);
      setPreguntaToDelete(null);
    } catch (error: any) {
      showNotification(
        "error",
        "Error",
        error.response?.data?.message || "Error al eliminar"
      );
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setPreguntaToDelete(null);
  };

  // Función para filtrar datos
  const getFilteredData = () => {
    let filteredData = preguntas;

    // Filtrar por texto de búsqueda
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      filteredData = filteredData.filter(
        (pregunta) =>
          pregunta.contenido.toLowerCase().includes(searchLower) ||
          pregunta.respuesta.toLowerCase().includes(searchLower) ||
          pregunta.explicacion.toLowerCase().includes(searchLower) ||
          getMundoName(pregunta.mundoHistoriaId)
            .toLowerCase()
            .includes(searchLower) ||
          getEtapaName(pregunta.etapaHistoriaId)
            .toLowerCase()
            .includes(searchLower)
      );
    }

    // Filtrar por mundo seleccionado
    if (filters.selectedMundo) {
      const mundoId = parseInt(filters.selectedMundo);

      filteredData = filteredData.filter((pregunta) => {
        return pregunta.mundoHistoriaId === mundoId;
      });
    }

    // Filtrar por etapa seleccionada
    if (filters.selectedEtapa) {
      const etapaId = parseInt(filters.selectedEtapa);
      filteredData = filteredData.filter((pregunta) => {
        return pregunta.etapaHistoriaId === etapaId;
      });
    }

    // Filtrar por vigencia
    if (filters.vigenciaFilter !== "todos") {
      const isVigente = filters.vigenciaFilter === "vigentes";
      filteredData = filteredData.filter(
        (pregunta) => pregunta.vigente === isVigente
      );
    }

    // Filtrar por gráfico
    if (filters.tieneGraficoFilter !== "todos") {
      const tieneGrafico = filters.tieneGraficoFilter === "con-grafico";
      filteredData = filteredData.filter((pregunta) => {
        const tieneDatosGrafico =
          pregunta.datosGrafico &&
          Object.keys(pregunta.datosGrafico).length > 0;
        return tieneDatosGrafico === tieneGrafico;
      });
    }

    return filteredData;
  };

  // Función auxiliar para ordenar preguntas por mundo, etapa y orden
  // Usa ID como criterio de desempate para mantener orden estable
  const sortPreguntas = (preguntas: ModoHistoriaDto[]) => {
    return [...preguntas].sort((a, b) => {
      // Primero por orden del mundo
      const mundoA = mundos.find(
        (m) => m.idMundoHistoria === (a.mundoHistoriaId || 0)
      );
      const mundoB = mundos.find(
        (m) => m.idMundoHistoria === (b.mundoHistoriaId || 0)
      );
      const ordenMundoA = mundoA?.orden || 0;
      const ordenMundoB = mundoB?.orden || 0;

      if (ordenMundoA !== ordenMundoB) {
        return ordenMundoA - ordenMundoB;
      }

      // Luego por orden de la etapa
      const etapaA = etapas.find(
        (e) => e.idEtapaHistoria === a.etapaHistoriaId
      );
      const etapaB = etapas.find(
        (e) => e.idEtapaHistoria === b.etapaHistoriaId
      );
      const ordenEtapaA = etapaA?.orden || 0;
      const ordenEtapaB = etapaB?.orden || 0;

      if (ordenEtapaA !== ordenEtapaB) {
        return ordenEtapaA - ordenEtapaB;
      }

      // Finalmente por orden de la pregunta
      const ordenPreguntaA = a.orden || 0;
      const ordenPreguntaB = b.orden || 0;

      if (ordenPreguntaA !== ordenPreguntaB) {
        return ordenPreguntaA - ordenPreguntaB;
      }

      // Criterio de desempate: usar ID para mantener orden estable
      // Esto evita que preguntas con el mismo orden cambien de posición
      return a.idPreguntaOficial - b.idPreguntaOficial;
    });
  };

  // Obtener datos filtrados y ordenados para la tabla
  const getFilteredAndSortedData = () => {
    const filteredData = getFilteredData();
    // Solo reordenar si es necesario (cambió el orden en la última edición)
    // Si no cambió el orden, mantener el orden del array original
    if (reordenarDespuesEdicion) {
      return sortPreguntas([...filteredData]);
    }
    // Mantener el orden actual del array (ya está ordenado desde la carga inicial)
    return filteredData;
  };

  // Calcular datos de la página actual
  const getCurrentPageData = () => {
    const filteredData = getFilteredAndSortedData();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  };

  // Resetear página cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.searchText,
    filters.selectedMundo,
    filters.selectedEtapa,
    filters.vigenciaFilter,
    filters.tieneGraficoFilter,
  ]);

  // Ajustar página si está fuera de rango después de filtrar
  useEffect(() => {
    const filteredData = getFilteredAndSortedData();
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preguntas.length, itemsPerPage]);

  // Funciones para manejo de importación
  const handleImportSuccess = () => {
    showNotification("success", "Éxito", "Preguntas importadas correctamente");
    cargarDatos();
  };

  // Funciones para edición masiva
  const handleBulkEditSuccess = () => {
    showNotification(
      "success",
      "Éxito",
      "Preguntas actualizadas correctamente"
    );
    setSelectedItems([]);
    cargarDatos();
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-blue-600">
                Modo Historia - Preguntas Oficiales
              </h1>
              <p className="text-gray-600">
                Gestiona las preguntas oficiales del modo historia
              </p>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg border border-blue-100">
            <div className="p-6">
              <div className="text-center py-8">
                <p>Cargando...</p>
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-blue-600">
                Modo Historia - Preguntas Oficiales
              </h1>
              <p className="text-gray-600">
                Gestiona las preguntas oficiales del modo historia
              </p>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg border border-blue-100">
            <div className="p-6">
              <div className="text-center text-red-600 py-8">
                <h2 className="text-xl font-semibold mb-2">Error</h2>
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header con botones */}
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">
            Gestión de Preguntas Oficiales
          </h1>
          <div className="flex space-x-2">
            {selectedItems.length > 0 && (
              <Button
                onClick={() => setShowBulkEditModal(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Editar {selectedItems.length} seleccionada(s)
              </Button>
            )}
            <Button
              onClick={openCreateModal}
              className="bg-green-600 hover:bg-green-700"
            >
              Crear Pregunta
            </Button>
            <Button
              onClick={() => setShowImportModal(true)}
              variant="outline"
              className="border-blue-500 text-blue-600 hover:bg-blue-50"
            >
              Importar JSON
            </Button>
          </div>
        </div>

        {/* Filtros */}
        <ModoHistoriaFilter
          filters={filters}
          onFiltersChange={setFilters}
          mundos={mundos.map((mundo) => ({
            id: mundo.idMundoHistoria,
            name: mundo.nombre,
          }))}
          etapas={etapas.map((etapa) => ({
            id: etapa.idEtapaHistoria,
            name: etapa.nombre,
            mundoId: etapa.mundoId,
          }))}
        />

        {/* Tabla de datos */}
        <PreguntasOficialesTable
          preguntas={getCurrentPageData()}
          loading={loading}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onView={handleView}
          getMundoName={getMundoName}
          getEtapaName={getEtapaName}
          totalPreguntas={getFilteredAndSortedData().length}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />

        {/* Modal de creación/edición */}
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          title={
            isEditing ? "Editar Pregunta Oficial" : "Crear Pregunta Oficial"
          }
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Mostrar ID cuando se está editando */}
            {isEditing && editingId && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">ID de la Pregunta:</span>{" "}
                  <span className="text-blue-600 font-semibold">
                    {editingId}
                  </span>
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="mundoHistoriaId">Mundo *</Label>
              <select
                id="mundoHistoriaId"
                value={formData.mundoHistoriaId}
                onChange={(e) => {
                  const mundoId = parseInt(e.target.value);
                  setFormData({ ...formData, mundoHistoriaId: e.target.value });
                  if (mundoId) {
                    handleMundoChange(mundoId);
                  }
                }}
                className="w-full p-2 border rounded"
                required
              >
                <option value="">Seleccionar mundo</option>
                {mundos.map((mundo) => (
                  <option
                    key={mundo.idMundoHistoria}
                    value={mundo.idMundoHistoria.toString()}
                  >
                    {mundo.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="etapaHistoriaId">Etapa *</Label>
              <select
                id="etapaHistoriaId"
                value={formData.etapaHistoriaId}
                onChange={(e) =>
                  setFormData({ ...formData, etapaHistoriaId: e.target.value })
                }
                className="w-full p-2 border rounded"
                required
              >
                <option value="">Seleccionar etapa</option>
                {etapasParaEdicion.map((etapa) => (
                  <option
                    key={etapa.idEtapaHistoria}
                    value={etapa.idEtapaHistoria}
                  >
                    {etapa.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="preguntaOrden">Orden de la pregunta *</Label>
              <Input
                id="preguntaOrden"
                type="number"
                min="1"
                value={formData.preguntaOrden || 1}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    preguntaOrden: Number(e.target.value),
                  })
                }
                required
                placeholder="Orden de la pregunta"
                className="w-full"
              />
            </div>

            <div>
              <Label htmlFor="contenido">Pregunta *</Label>
              <Textarea
                id="contenido"
                value={formData.contenido}
                onChange={(e) =>
                  setFormData({ ...formData, contenido: e.target.value })
                }
                required
                placeholder="Escribe la pregunta aquí..."
                rows={3}
              />
            </div>

            <div>
              <Label>Alternativas *</Label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(formData.alternativas).map(([key, value]) => (
                  <div key={key}>
                    <Label htmlFor={`alternativa-${key}`}>{key}</Label>
                    <textarea
                      id={`alternativa-${key}`}
                      value={value}
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setFormData({
                          ...formData,
                          alternativas: {
                            ...formData.alternativas,
                            [key]: e.target.value,
                          },
                        })
                      }
                      required
                      placeholder={`Alternativa ${key}`}
                      className="w-full p-2 border rounded min-h-[60px] resize-vertical"
                      rows={2}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="respuesta">Respuesta correcta *</Label>
              <select
                id="respuesta"
                value={formData.respuesta}
                onChange={(e) =>
                  setFormData({ ...formData, respuesta: e.target.value })
                }
                className="w-full p-2 border rounded"
                required
              >
                <option value="">Seleccionar respuesta</option>
                {Object.keys(formData.alternativas).map((key) => (
                  <option key={key} value={key}>
                    {key}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="explicacion">Explicación</Label>
              <Textarea
                id="explicacion"
                value={formData.explicacion}
                onChange={(e) =>
                  setFormData({ ...formData, explicacion: e.target.value })
                }
                placeholder="Explicación de la respuesta (opcional)"
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="tieneImagen">¿Tiene imagen?</Label>
              <select
                id="tieneImagen"
                value={formData.tieneImagen ? "true" : "false"}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    tieneImagen: e.target.value === "true",
                    imagen: null, // Limpiar imagen cuando se cambia el estado
                  })
                }
                className="w-full p-2 border rounded"
              >
                <option value="false">No</option>
                <option value="true">Sí</option>
              </select>
            </div>

            {formData.tieneImagen && (
              <div>
                <Label htmlFor="imagen">Imagen</Label>
                <Input
                  id="imagen"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setFormData({ ...formData, imagen: file });
                    }
                  }}
                  className="w-full"
                />
                {formData.imagen && (
                  <p className="text-sm text-gray-600 mt-1">
                    Archivo seleccionado: {formData.imagen.name}
                  </p>
                )}
              </div>
            )}

            <div>
              <Label htmlFor="datosGrafico">Datos Gráficos (JSON)</Label>
              <Textarea
                id="datosGrafico"
                value={formData.datosGrafico}
                onChange={(e) =>
                  setFormData({ ...formData, datosGrafico: e.target.value })
                }
                placeholder='Ejemplo: {"kind": "geoscene", "version": 1, "viewport": {"xmin": -2, "xmax": 10, "ymin": -3, "ymax": 8}}'
                rows={6}
                className="w-full font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                Ingresa los datos JSON para gráficos interactivos (opcional)
              </p>
            </div>

            <div>
              <Label htmlFor="vigente">Estado de Vigencia</Label>
              <select
                id="vigente"
                value={formData.vigente ? "true" : "false"}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    vigente: e.target.value === "true",
                  })
                }
                className="w-full p-2 border rounded"
              >
                <option value="true">Vigente</option>
                <option value="false">No Vigente</option>
              </select>
            </div>

            {/* Checkbox de revisión */}
            {isEditing && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.revisada}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        revisada: e.target.checked,
                      })
                    }
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-900">
                      Marcar como revisada
                    </span>
                    <p className="text-xs text-gray-600">
                      Marca esta casilla si has verificado que la pregunta está
                      completamente correcta
                    </p>
                  </div>
                </label>
              </div>
            )}

            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={closeModal}
                disabled={isSaving}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? (
                  <span className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {isEditing ? "Actualizando..." : "Creando..."}
                  </span>
                ) : isEditing ? (
                  "Actualizar"
                ) : (
                  "Crear"
                )}
              </Button>
            </div>
          </form>
        </Modal>

        {/* Notificación */}
        <Notification
          isOpen={notification.isOpen}
          type={notification.type}
          title={notification.title}
          message={notification.message}
          onClose={closeNotification}
        />

        {/* Confirmación de eliminación */}
        {showDeleteConfirm && preguntaToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 border border-blue-100">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-6 w-6 text-red-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-lg font-medium text-gray-900">
                      Confirmar desactivación
                    </h3>
                  </div>
                </div>
                <div className="mb-6">
                  <p className="text-sm text-gray-500">
                    ¿Estás seguro de que quieres desactivar esta pregunta
                    oficial? La pregunta se marcará como no vigente pero no se
                    eliminará físicamente.
                  </p>
                  {preguntaToDelete && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-md">
                      <p className="text-sm font-medium text-gray-900">
                        Pregunta #{preguntaToDelete.orden || "-"} -{" "}
                        {getMundoName(preguntaToDelete.mundoHistoriaId)} -{" "}
                        {getEtapaName(preguntaToDelete.etapaHistoriaId)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                        {preguntaToDelete.contenido.substring(0, 100)}...
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleDeleteConfirm}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-md"
                  >
                    Desactivar
                  </button>
                  <button
                    onClick={handleDeleteCancel}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de Importación JSON */}
        <ImportarJSONModalModoHistoria
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
          onSuccess={handleImportSuccess}
        />

        {/* Modal de Vista de Pregunta */}
        <PreguntaOficialDetailModal
          pregunta={editingPregunta}
          isOpen={isViewModalOpen}
          onClose={() => {
            setIsViewModalOpen(false);
            setEditingPregunta(null);
          }}
          mode="view"
        />

        {/* Modal de Edición Masiva */}
        <BulkEditModal
          isOpen={showBulkEditModal}
          onClose={() => setShowBulkEditModal(false)}
          selectedItems={selectedItems}
          onSuccess={handleBulkEditSuccess}
          mundos={mundos}
          etapas={etapas}
        />
      </div>
    </AdminLayout>
  );
};
